/**
 * Watching a clip with the room.
 *
 * The interesting part is not the player, it is the clock.
 *
 * A session stores an anchor - a position, the server time it was true at,
 * and whether it is running - so where the clip *should* be is arithmetic
 * anybody can do. The catch is "server time": this machine's clock might be
 * four minutes fast, and a player that trusts it would sit four minutes into
 * a two minute clip.
 *
 * So the client measures the offset the way NTP does. Every heartbeat is a
 * round trip: note the time before, read the server's time in the reply, note
 * the time after, and assume the reply took half the round trip to come back.
 * That gives an estimate of (their clock - our clock), and the smallest round
 * trip seen so far wins, because the shortest trip is the least distorted.
 *
 * Correction happens in two speeds, because the two failures feel completely
 * different:
 *
 *   - under the server's nudgeMax, play 4% faster or slower until it catches
 *     up. Nobody hears 4%; everybody sees a jump.
 *   - over it, seek. That is a stall or a backgrounded tab, and pretending
 *     otherwise means a minute of wrong-speed audio on the way back.
 *
 * Everything else is deliberately small: whoever started it drives, they can
 * hand it over, and anybody can see how far out everybody else is - which is
 * the one piece of feedback that stops "are you seeing this?" in voice chat.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, serverUrl, socket } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useWatchFrame, SizeGrip } from './WatchFrame';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { absolute } from './user';
import { Button, useToast } from './ui';
import type { PublicUser } from '@/lib/types';

type Anchor = { position: number; at: number; playing: boolean };

export type WatchSession = {
  id: string;
  scope: 'party' | 'crew';
  channelId: string;
  /** An uploaded clip Pugo is serving, or somebody else's player in a frame. */
  kind: 'clip' | 'link';
  url: string;
  watchUrl: string | null;
  provider: string | null;
  providerName: string | null;
  type: string;
  title: string;
  duration: number | null;
  anchor: Anchor;
  serverNow: number;
  nudgeMax: number;
  driverId: string;
  canDrive: boolean;
  startedBy: PublicUser | null;
  watchers: { user: PublicUser; drift: number; at: number }[];
  /** Lined up after this one. Anybody adds; only the driver moves it on. */
  queue: { id: string; title: string; providerName: string | null; watchUrl: string | null; by: string }[];
  startedAt: number;
};

/** How long a clip may be, matching watch.js. Checked before the upload. */
const CLIP_SECONDS = 600;

/** How fast a small correction is made. 4% is under the threshold of notice. */
const NUDGE_RATE = 0.04;

export function WatchTogether({ scope, channelId, canStart }: {
  scope: 'party' | 'crew';
  channelId: string;
  /** Whether this person may put a clip on at all. */
  canStart: boolean;
}) {
  const me = useSession((s) => s.me);
  const toast = useToast();
  const queryClient = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [session, setSession] = useState<WatchSession | null>(null);
  const [uploading, setUploading] = useState(false);
  const [link, setLink] = useState('');

  /**
   * Show something that lives somewhere else.
   *
   * Nothing is uploaded and nothing is transcoded - the address is read on
   * the server into a provider and an id, and everybody's browser loads that
   * site's own player. Which is also why the clock is advisory for a link;
   * see the note in the player.
   */
  async function startLink() {
    setUploading(true);
    try {
      const res = await api.post<{ watch: WatchSession }>('/api/watch/' + scope + '/' + channelId, { link: link.trim() });
      setSession(res.watch);
      setLink('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setUploading(false);
    }
  }

  /* Load whatever is already playing, and follow it. */
  useEffect(() => {
    let gone = false;
    void api.get<{ watch: WatchSession | null }>('/api/watch/' + scope + '/' + channelId)
      .then((r) => { if (!gone) setSession(r.watch); })
      .catch(() => { /* not in the room any more */ });

    const off = [
      socket.on('watch:started', (d: { watch: WatchSession }) => {
        if (d.watch.scope === scope && d.watch.channelId === channelId) setSession(d.watch);
      }),
      socket.on('watch:moved', (d: { watch: WatchSession }) => {
        if (d.watch.scope === scope && d.watch.channelId === channelId) setSession(d.watch);
      }),
      socket.on('watch:ended', (d: { scope: string; channelId: string }) => {
        if (d.scope === scope && d.channelId === channelId) setSession(null);
      })
    ];
    return () => { gone = true; off.forEach((fn) => fn()); };
  }, [scope, channelId]);

  async function start(f: File) {
    setUploading(true);
    try {
      /* Length first, on this machine: uploading 200MB to be told it is too
         long is a minute of somebody's evening for an answer we already had. */
      const seconds = await durationOf(f).catch(() => null);
      if (seconds && seconds > CLIP_SECONDS) {
        throw new Error('That is ' + Math.round(seconds / 60) + ' minutes. Watch together is for clips under ' + Math.round(CLIP_SECONDS / 60) + '.');
      }

      const up = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': f.type || 'application/octet-stream',
          'X-File-Type': f.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(f.name),
          'X-File-Kind': 'attachment'
        },
        body: f
      });
      if (!up.ok) {
        const text = await up.text();
        throw new Error(tidy(text) || 'That clip would not upload.');
      }
      const saved = await up.json();
      const res = await api.post<{ watch: WatchSession }>('/api/watch/' + scope + '/' + channelId, {
        fileId: saved.file?.id ?? saved.id,
        duration: seconds,
        title: f.name
      });
      setSession(res.watch);
      void queryClient.invalidateQueries({ queryKey: ['party', channelId] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setUploading(false);
      if (file.current) file.current.value = '';
    }
  }

  if (!session) {
    if (!canStart) return null;
    return (
      <section className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-3.5 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        <div className="flex items-center gap-2">
          <Icon name="video" size={16} className="flex-none text-accent" />
          <h3 className="m-0 flex-1 text-[14px] font-bold text-ink">Watch together</h3>
        </div>
        <p className="mt-1.5 mb-2.5 text-[12.5px] leading-snug text-mute">
          Paste a YouTube, Twitch or Vimeo link, or put one of your own clips on. Everybody sees the
          same second and whoever is showing it has the controls.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); void startLink(); }}
          className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2"
        >
          <Icon name="link" size={15} className="flex-none text-ghost" />
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste a video link…"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ghost"
          />
          <Button size="sm" kind="primary" disabled={uploading || !link.trim()}>
            {uploading ? 'Starting…' : 'Show it'}
          </Button>
        </form>

        <div className="mt-2 flex items-center gap-2">
          <span className="text-[11.5px] text-ghost">or</span>
          <Button size="sm" kind="ghost" disabled={uploading} onClick={() => file.current?.click()}>
            Upload a clip
          </Button>
        </div>

        <input
          ref={file}
          type="file"
          accept="video/mp4,video/webm"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void start(f); }}
        />
      </section>
    );
  }

  return <Player session={session} me={me?.id ?? null} onGone={() => setSession(null)} onSession={setSession} />;
}

/* ----------------------------------------------------------- the player */

function Player({ session, me, onGone, onSession }: {
  session: WatchSession;
  me: string | null;
  onGone: () => void;
  onSession: (s: WatchSession) => void;
}) {
  const toast = useToast();
  const video = useRef<HTMLVideoElement>(null);
  /* (their clock - our clock), and the shortest round trip that produced it. */
  const offset = useRef(0);
  const bestTrip = useRef(Infinity);
  const [drift, setDrift] = useState(0);
  const [ready, setReady] = useState(false);
  /* Folded hides the picture and keeps the room; big gives it the height it
     deserves when everybody is actually watching rather than half-watching. */
  const [folded, setFolded] = useState(false);
  const [big, setBig] = useState(false);
  /* Docked, floating or fullscreen, and where the floating one sits. */
  const frame = useWatchFrame();
  /* Your own ears. Everybody sets their own - it is the only part of a
     watch party that is not the room's decision. */
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  /*
   * The scrub bar and the clock are written straight into the DOM from the
   * heartbeat that was already running, four times a second. Holding the
   * position in state instead would re-render the whole player - and
   * everything in it, including the avatars - four times a second for as
   * long as the clip runs, which is exactly the kind of thing that makes a
   * laptop warm.
   */
  const seek = useRef<HTMLDivElement>(null);
  const clock = useRef<HTMLSpanElement>(null);

  const serverNow = useCallback(() => Date.now() + offset.current, []);

  /** Where the clip should be, right now, by the room's clock. */
  const target = useCallback(() => {
    const a = session.anchor;
    if (!a.playing) return a.position;
    const on = a.position + (serverNow() - a.at) / 1000;
    return session.duration ? Math.min(on, session.duration) : on;
  }, [session.anchor, session.duration, serverNow]);

  /*
   * The heartbeat: says we are here, and measures the clock offset.
   *
   * The smallest round trip wins. A reply that took 40ms tells you far more
   * about the other clock than one that took 900ms behind a stalled upload,
   * and taking the minimum rather than an average is what stops one slow
   * request dragging the estimate off for the next ten minutes.
   */
  useEffect(() => {
    let stop = false;
    async function beat() {
      const before = Date.now();
      try {
        const res = await api.post<{ watch: WatchSession | null; serverNow: number }>(
          '/api/watch/' + session.scope + '/' + session.channelId + '/here',
          { drift }
        );
        const after = Date.now();
        const trip = after - before;
        if (trip < bestTrip.current) {
          bestTrip.current = trip;
          offset.current = res.serverNow + trip / 2 - after;
        }
        if (stop) return;
        if (!res.watch) { onGone(); return; }
        onSession(res.watch);
      } catch (err) { /* a beat that misses is not worth a toast */ }
    }
    void beat();
    const timer = setInterval(() => void beat(), 5000);
    return () => { stop = true; clearInterval(timer); };
    // drift is read inside and changes constantly; re-subscribing on it would
    // restart the timer every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.scope, session.channelId]);

  /*
   * Staying with the room.
   *
   * Runs four times a second rather than every frame: correcting a clock is
   * not an animation, and a 250ms cadence is well under what anybody can
   * hear as a speed change.
   */
  useEffect(() => {
    const el = video.current;
    /* A linked player is not ours to nudge - there is no element to read. */
    if (!el || session.kind === 'link') return;
    const timer = setInterval(() => {
      const want = target();
      const have = el.currentTime;
      const out = have - want;
      setDrift(out);

      if (!session.anchor.playing) {
        if (!el.paused) el.pause();
        if (Math.abs(out) > 0.3) el.currentTime = want;
        el.playbackRate = 1;
        return;
      }

      if (el.paused) void el.play().catch(() => { /* waiting on a gesture */ });

      if (Math.abs(out) > session.nudgeMax) {
        /* Too far gone to nudge: a stall, or a tab that was asleep. */
        el.currentTime = want;
        el.playbackRate = 1;
      } else if (Math.abs(out) > 0.12) {
        el.playbackRate = out > 0 ? 1 - NUDGE_RATE : 1 + NUDGE_RATE;
      } else {
        el.playbackRate = 1;
      }
    }, 250);
    return () => clearInterval(timer);
  }, [session.anchor, session.nudgeMax, session.kind, target]);

  /*
   * The scrub bar and the clock.
   *
   * Written into the DOM rather than held in state, for the reason given
   * where the refs are declared. A linked player has no element to read, so
   * that one is drawn from where the room says it should be instead.
   */
  useEffect(() => {
    const paint = () => {
      const len = session.duration || 0;
      const at = session.kind === 'link' ? target() : (video.current?.currentTime ?? 0);
      if (seek.current) seek.current.style.width = len > 0 ? Math.min(100, Math.max(0, (at / len) * 100)) + '%' : '0%';
      if (clock.current) clock.current.textContent = len > 0 ? clockText(at) + ' / ' + clockText(len) : clockText(at);
    };
    paint();
    const timer = setInterval(paint, 250);
    return () => clearInterval(timer);
  }, [session.duration, session.kind, target]);

  /* Your volume is yours, and it is not worth a round trip. */
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    el.muted = muted;
    el.volume = volume;
  }, [muted, volume, ready]);

  /* What somebody is typing into the queue box. */
  const [upNext, setUpNext] = useState('');
  const [queuing, setQueuing] = useState(false);

  async function queueIt() {
    const link = upNext.trim();
    if (!link || queuing) return;
    setQueuing(true);
    try {
      const res = await api.post<{ watch: WatchSession }>(
        '/api/watch/' + session.scope + '/' + session.channelId + '/queue', { link }
      );
      onSession(res.watch);
      setUpNext('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setQueuing(false);
    }
  }

  async function dropFromQueue(id: string) {
    try {
      const res = await api.del<{ watch: WatchSession }>(
        '/api/watch/' + session.scope + '/' + session.channelId + '/queue/' + id
      );
      onSession(res.watch);
    } catch (err) { toast((err as Error).message, 'bad'); }
  }

  async function playNext() {
    try {
      const res = await api.post<{ watch: WatchSession }>(
        '/api/watch/' + session.scope + '/' + session.channelId + '/next', {}
      );
      onSession(res.watch);
    } catch (err) { toast((err as Error).message, 'bad'); }
  }

  /** Seeking the room from a press on the bar. Only the driver may. */
  function scrubTo(e: React.MouseEvent<HTMLDivElement>) {
    if (!session.canDrive || !session.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    const at = ((e.clientX - r.left) / Math.max(1, r.width)) * session.duration;
    void drive(Math.min(session.duration, Math.max(0, at)), session.anchor.playing);
  }

  async function drive(position: number, playing: boolean) {
    try {
      const res = await api.post<{ watch: WatchSession }>(
        '/api/watch/' + session.scope + '/' + session.channelId + '/drive',
        { position, playing }
      );
      onSession(res.watch);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function stop() {
    try {
      await api.del('/api/watch/' + session.scope + '/' + session.channelId);
      onGone();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const playing = session.anchor.playing;
  const linked = session.kind === 'link';
  const others = session.watchers.filter((w) => w.user.id !== me);
  const worst = others.reduce((a, w) => Math.max(a, Math.abs(w.drift)), 0);
  /* Floating means a window of its own; fullscreen wins over both. */
  const floating = frame.hold === 'float' && !frame.full;
  const filling = floating || frame.full;

  const iconBtn = 'grid h-7 w-7 flex-none place-items-center rounded-[8px] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]';

  return (
    <section
      ref={frame.shell}
      style={floating ? { left: frame.box.x, top: frame.box.y, width: frame.box.w, height: frame.box.h } : undefined}
      className={cn(
        /* flex-none is load-bearing while it is docked. The column it sits
           in is a flex column, overflow-hidden turns off a flex item's
           automatic minimum size, and between the two the panel was being
           shrunk to nothing - present in the page, every control in it, and
           zero pixels tall. */
        'pugo-panel flex flex-none flex-col overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]',
        floating && 'fixed z-[340] shadow-[var(--shadow-3)]',
        frame.full && 'fixed inset-0 z-[400] rounded-none'
      )}
    >
      {/*
        The bar across the top. In a floating window it is also the handle
        you drag it by - touch-none so a trackpad drag moves the window
        rather than scrolling the page behind it.
      */}
      <div
        onPointerDown={floating ? frame.grab('move') : undefined}
        className={cn(
          'flex flex-none items-center gap-2 px-3 py-2.5',
          floating && 'cursor-grab touch-none select-none active:cursor-grabbing'
        )}
      >
        <Icon name={linked ? 'external' : 'video'} size={15} className="flex-none text-accent" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-ink">{session.title}</span>
        {session.providerName ? (
          <span className="hidden flex-none rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-semibold text-mute sm:inline">
            {session.providerName}
          </span>
        ) : null}
        <span className="flex flex-none items-center gap-1 text-[11.5px] font-semibold text-good">
          <i aria-hidden className="h-1.5 w-1.5 rounded-full bg-good" />
          Together
        </span>

        <button
          type="button"
          aria-label={floating ? 'Put it back in the column' : 'Pop it out into its own window'}
          title={floating ? 'Put it back' : 'Pop out'}
          aria-pressed={floating}
          onClick={() => frame.setHold(floating ? 'dock' : 'float')}
          className={iconBtn}
        >
          <Icon name={floating ? 'restore' : 'external'} size={14} />
        </button>
        {!filling ? (
          <button
            type="button"
            aria-label={big ? 'Shrink' : 'Make it bigger'}
            title={big ? 'Shrink' : 'Make it bigger'}
            onClick={() => setBig((v) => !v)}
            className={iconBtn}
          >
            <Icon name={big ? 'restore' : 'expand'} size={14} />
          </button>
        ) : null}
        <button
          type="button"
          aria-label={frame.full ? 'Leave fullscreen' : 'Fullscreen'}
          title={frame.full ? 'Leave fullscreen' : 'Fullscreen'}
          aria-pressed={frame.full}
          onClick={frame.toggleFull}
          className={iconBtn}
        >
          <Icon name={frame.full ? 'minimize' : 'expand'} size={14} />
        </button>
        <button
          type="button"
          aria-label={folded ? 'Show the video' : 'Hide the video'}
          title={folded ? 'Show the video' : 'Hide the video'}
          onClick={() => setFolded((v) => !v)}
          className={iconBtn}
        >
          <Icon name={folded ? 'chevronD' : 'minimize'} size={14} />
        </button>
      </div>

      <div
        className={cn(
          'relative bg-black',
          filling ? 'min-h-0 flex-1' : 'flex-none',
          folded && !filling && 'hidden'
        )}
      >
        {linked ? (
          /*
           * Somebody else's player, in a frame.
           *
           * Pugo can start it, stop it and seek it, and everybody gets the
           * same instruction at the same moment - but it cannot read the
           * frame's clock, so there is no drift correction here and the line
           * below says so rather than claiming a precision it does not have.
           */
          <iframe
            src={session.url}
            title={session.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            className={cn(
              'block w-full border-0',
              filling ? 'h-full' : big ? 'h-[62vh] max-h-[680px]' : 'aspect-video max-h-[420px]'
            )}
          />
        ) : (
        <video
          ref={video}
          src={absolute(session.url)}
          className={cn('block w-full', filling ? 'h-full object-contain' : big ? 'h-[62vh] max-h-[680px] object-contain' : 'max-h-[420px]')}
          playsInline
          onLoadedMetadata={() => {
            setReady(true);
            const el = video.current;
            if (el) el.currentTime = target();
          }}
          /* The room drives, not the element. A double-click on the video
             would otherwise seek this one machine out of step silently. */
          onSeeking={() => {
            const el = video.current;
            if (!el || session.canDrive) return;
            const want = target();
            if (Math.abs(el.currentTime - want) > session.nudgeMax) el.currentTime = want;
          }}
        />
        )}
        {!linked && !ready ? (
          <span className="absolute inset-0 grid place-items-center text-[12.5px] text-white/70">Loading the clip…</span>
        ) : null}
      </div>

      {/*
        The controls.

        They are part of the panel rather than an overlay on the picture, so
        there is nothing to fade out and nothing to move the mouse to bring
        back. They are simply always here, in every shape - docked, popped
        out, folded and fullscreen.
      */}
      <div className={cn('flex flex-none flex-col gap-1.5 px-3 py-2', frame.full && 'bg-surface-1')}>
        <div className="flex items-center gap-2">
          <span ref={clock} className="flex-none tabular-nums text-[11px] font-semibold text-mute">0:00</span>
          <div
            role={session.canDrive ? 'slider' : 'progressbar'}
            aria-label="Position"
            aria-valuemin={0}
            aria-valuemax={Math.round(session.duration || 0)}
            tabIndex={session.canDrive ? 0 : -1}
            onClick={scrubTo}
            onKeyDown={(e) => {
              if (!session.canDrive) return;
              const at = video.current?.currentTime ?? 0;
              if (e.key === 'ArrowLeft') { e.preventDefault(); void drive(Math.max(0, at - 5), playing); }
              if (e.key === 'ArrowRight') { e.preventDefault(); void drive(at + 5, playing); }
            }}
            className={cn(
              'relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-3 outline-none',
              session.canDrive ? 'cursor-pointer focus-visible:shadow-[0_0_0_2px_var(--accent)]' : 'cursor-default'
            )}
          >
            <div ref={seek} className="absolute inset-y-0 left-0 w-0 rounded-full bg-accent" />
          </div>
          <button
            type="button"
            aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
            title={muted || volume === 0 ? 'Unmute' : 'Mute'}
            onClick={() => setMuted((v) => !v)}
            disabled={linked}
            className={cn(iconBtn, 'h-6 w-6 disabled:cursor-not-allowed disabled:opacity-40')}
          >
            <Icon name={muted || volume === 0 ? 'volumeOff' : 'volume'} size={13} />
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={muted ? 0 : volume}
            disabled={linked}
            aria-label="Volume"
            onChange={(e) => { setVolume(Number(e.target.value)); setMuted(Number(e.target.value) === 0); }}
            className="h-1.5 w-[74px] flex-none accent-[var(--accent)] disabled:opacity-40"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
        {session.canDrive ? (
          <>
            <button
              type="button"
              aria-label={playing ? 'Pause for everybody' : 'Play for everybody'}
              onClick={() => void drive(video.current?.currentTime ?? 0, !playing)}
              className="grid h-8 w-8 flex-none place-items-center rounded-full bg-accent text-accent-fg outline-none transition-[background-color] hover:bg-accent-hi"
            >
              <Icon name={playing ? 'pause' : 'play'} size={15} />
            </button>
            <button
              type="button"
              onClick={() => void drive(Math.max(0, (video.current?.currentTime ?? 0) - 10), playing)}
              className="rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
            >
              −10s
            </button>
            <button
              type="button"
              onClick={() => void drive((video.current?.currentTime ?? 0) + 10, playing)}
              className="rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
            >
              +10s
            </button>
            <button
              type="button"
              onClick={() => void drive(0, true)}
              className="hidden rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink sm:block"
            >
              From the start
            </button>
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-[12px] text-mute">
            <Icon name={playing ? 'play' : 'pause'} size={13} />
            {session.startedBy?.displayName ?? 'Somebody'} is driving
          </span>
        )}

        <span className="flex-1" />

        {/* How together the room actually is. Shown rather than assumed: this
            is the number that settles "is it just me". */}
        {linked ? (
          <span
            className="flex flex-none items-center gap-1.5 text-[11.5px] font-semibold text-mute"
            title={'Pugo starts, stops and seeks the player for everybody, but cannot read its clock - so this one is kept together rather than measured.'}
          >
            <Icon name="waves" size={12} />
            Kept together
          </span>
        ) : (
          <span
            className={cn(
              'flex flex-none items-center gap-1.5 text-[11.5px] font-semibold',
              Math.abs(drift) > session.nudgeMax ? 'text-warn' : 'text-mute'
            )}
            title={'You are ' + drift.toFixed(1) + 's from the room' + (worst ? '; the furthest out is ' + worst.toFixed(1) + 's' : '')}
          >
            <Icon name="waves" size={12} />
            {Math.abs(drift) < 0.3 ? 'In sync' : (drift > 0 ? '+' : '') + drift.toFixed(1) + 's'}
          </span>
        )}

        {others.length ? (
          <span className="flex flex-none items-center">
            {others.slice(0, 4).map((w, i) => (
              <span key={w.user.id} className="rounded-full ring-2 ring-[var(--bg-1)]" style={{ marginLeft: i ? -7 : 0 }} title={w.user.displayName + ' · ' + w.drift.toFixed(1) + 's'}>
                <Avatar user={w.user} size={20} />
              </span>
            ))}
          </span>
        ) : null}

        {session.watchUrl ? (
          <a
            href={session.watchUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
          >
            Open
          </a>
        ) : null}
        {session.canDrive || session.startedBy?.id === me ? (
          <button
            type="button"
            onClick={() => void stop()}
            className="rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-bad"
          >
            Stop
          </button>
        ) : null}
        </div>
      </div>

      {/*
        What is on after this.

        A watch party used to end every time a clip did - somebody had to
        find the next link while everybody looked at a stopped frame.
        Anybody in the room can line one up; only whoever is driving moves
        it on, so a queue cannot be used to take the screen off them.

        Hidden while fullscreen: that is the one shape where the picture is
        the whole point.
      */}
      {!frame.full ? (
        <div className="flex flex-none flex-col gap-1.5 border-t border-line-soft px-3 py-2">
          {session.queue?.length ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {session.queue.map((q, i) => (
                <li key={q.id} className="flex items-center gap-2 text-[12px]">
                  <span className="w-[14px] flex-none text-right tabular-nums text-ghost">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-dim">{q.title}</span>
                  {q.providerName ? <span className="flex-none text-[11px] text-ghost">{q.providerName}</span> : null}
                  {q.by === me || session.canDrive ? (
                    <button
                      type="button"
                      aria-label={'Take ' + q.title + ' out of the queue'}
                      onClick={() => void dropFromQueue(q.id)}
                      className="grid h-5 w-5 flex-none place-items-center rounded-[6px] text-ghost outline-none transition-colors hover:bg-surface-2 hover:text-bad"
                    >
                      <Icon name="close" size={10} />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="flex items-center gap-1.5">
            <input
              value={upNext}
              onChange={(e) => setUpNext(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void queueIt(); } }}
              placeholder={session.queue?.length ? 'Add another link' : 'Paste a link to play after this'}
              aria-label="Add something to the queue"
              className="min-w-0 flex-1 rounded-[calc(9px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-1.5 text-[12px] outline-none shadow-[inset_0_0_0_1px_var(--line-soft)] focus:shadow-[inset_0_0_0_1px_var(--accent)]"
            />
            <button
              type="button"
              disabled={!upNext.trim() || queuing}
              onClick={() => void queueIt()}
              className="flex-none rounded-[calc(9px*var(--radius-mul,1))] px-2.5 py-1.5 text-[12px] font-bold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-40"
            >
              {queuing ? 'Adding…' : 'Queue'}
            </button>
            {session.canDrive && session.queue?.length ? (
              <button
                type="button"
                onClick={() => void playNext()}
                className="flex-none rounded-[calc(9px*var(--radius-mul,1))] bg-accent px-2.5 py-1.5 text-[12px] font-bold text-accent-fg outline-none transition-colors hover:bg-accent-hi"
              >
                Play next
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {floating ? <SizeGrip onGrab={frame.grab('size')} /> : null}
    </section>
  );
}

/** Seconds as a clock: 4:07, or 1:04:07 once it is worth an hour. */
function clockText(sec: number): string {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return (h ? h + ':' : '') + mm + ':' + String(r).padStart(2, '0');
}

/** How long a clip is, read from the file before it is uploaded. */
function durationOf(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(el.duration); };
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not a video this browser can read.')); };
    el.src = url;
  });
}

/** The server's plain-text errors come back as JSON or as a sentence. */
function tidy(text: string): string {
  try { return JSON.parse(text).error ?? ''; } catch { return text.slice(0, 160); }
}
